import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { Capacitor } from '@capacitor/core';

/** Maximum number of route subtrees to keep in memory at a time. */
const MAX_CACHE_SIZE = 15;

/**
 * TabReuseStrategy — preserves Angular component trees while switching tabs.
 *
 * - Only active on web/desktop (Capacitor native check + screen-width guard).
 * - Cache is size-bounded (MAX_CACHE_SIZE) to prevent unbounded memory growth.
 * - Cached component refs are explicitly destroyed when a tab is closed,
 *   ensuring zero memory leaks.
 * - All cache entries are wiped on logout (called via TabService.closeAll()).
 */
@Injectable()
export class TabReuseStrategy implements RouteReuseStrategy, OnDestroy {

    /** route-path key → detached Angular route handle */
    private readonly cache = new Map<string, DetachedRouteHandle>();
    /** Tracks insertion order for LRU eviction when cache is full */
    private insertionOrder: string[] = [];
    /**
     * Route IDs that must NOT be re-cached on the next detach.
     * Set by TabService before navigating away from a closed tab so Angular
     * cannot silently re-store the departing component tree.
     */
    private readonly skipOnceSet = new Set<string>();

    // ── RouteReuseStrategy contract ──────────────────────────────────────────

    /**
     * Called when leaving a route — should we save this subtree?
     * Return true only on web/desktop for routes that are real page routes.
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isEnabled()) { return false; }
        const path = route.routeConfig?.path;
        // Do not cache the root shell (''), auth, or super-admin routes
        if (path === undefined || path === null || path === '' || path === 'auth' || path === 'super-admin') {
            return false;
        }
        // Do not cache super-admin or auth pages — they use sidebar navigation (no tab bar),
        // so reattaching a cached tree skips ngOnInit and causes blank pages.
        const fullPath = this.buildKey(route);
        if (fullPath.startsWith('super-admin') || fullPath.startsWith('auth')) {
            return false;
        }
        if (this.isNoReusePath(fullPath)) {
            return false;
        }
        return true;
    }

    /** Store the detached route handle in the cache. */
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        const key = this.buildKey(route);
        if (!key) { return; }

        if (this.isNoReusePath(key)) {
            if (handle) {
                this.destroyHandle(handle);
            }
            this.destroyHandle(this.cache.get(key));
            this.cache.delete(key);
            this.insertionOrder = this.insertionOrder.filter(k => k !== key);
            return;
        }

        if (handle) {
            // If this route was marked to not be re-cached (tab was just closed),
            // destroy the handle and skip storing it.
            if (this.skipOnceSet.has(key)) {
                this.skipOnceSet.delete(key);
                this.destroyHandle(handle);
                return;
            }

            if (!this.cache.has(key) && this.cache.size >= MAX_CACHE_SIZE) {
                // Evict the oldest cached entry to stay within the memory budget
                const oldest = this.insertionOrder.shift();
                if (oldest) {
                    this.destroyHandle(this.cache.get(oldest));
                    this.cache.delete(oldest);
                }
            }
            this.cache.set(key, handle);
            if (!this.insertionOrder.includes(key)) {
                this.insertionOrder.push(key);
            }
        } else {
            // Angular passes null handle when it wants us to clear the entry
            this.destroyHandle(this.cache.get(key));
            this.cache.delete(key);
            this.insertionOrder = this.insertionOrder.filter(k => k !== key);
        }
    }

    /** Should the incoming route be restored from cache? */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isEnabled()) { return false; }
        const fullPath = this.buildKey(route);
        if (fullPath.startsWith('super-admin') || fullPath.startsWith('auth')) { return false; }
        if (this.isNoReusePath(fullPath)) { return false; }
        return this.cache.has(fullPath);
    }

    /** Return the cached handle (or null if not present). */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        return this.cache.get(this.buildKey(route)) ?? null;
    }

    /**
     * Should the same component instance be reused when navigating?
     * We compare the full resolved path so that navigating between two
     * different sidebar pages that share the same routeConfig reference
     * (e.g. lazy-loaded module roots) always triggers a real navigation.
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig;
    }

    // ── Public API (called by TabService) ────────────────────────────────────

    /**
     * Block the given route from being re-cached on its next store() call.
     * The check happens in store() using buildKey, so it works regardless of
     * which route level Angular chooses to detach.
     * Only call this for the ACTIVE tab (navigation will trigger store()).
     */
    preventDetach(routeId: string): void {
        this.skipOnceSet.add(routeId);
    }

    /**
     * Remove a route's cache entry WITHOUT destroying the componentRef.
     * Safe to call when the component is still live (active tab being closed).
     * Angular destroys the component during the navigation that follows.
     */
    removeCacheEntryOnly(routeId: string): void {
        const toDelete: string[] = [];
        for (const key of this.cache.keys()) {
            if (key === routeId || key.startsWith(routeId + '/')) {
                toDelete.push(key);
            }
        }
        for (const key of toDelete) {
            this.cache.delete(key);  // intentionally no destroyHandle — component is still mounted
        }
        this.insertionOrder = this.insertionOrder.filter(k => !toDelete.includes(k));
    }

    /**
     * Remove all cached entries whose key begins with `routeId`
     * (the first path segment, e.g. 'payments' or 'milk-collection').
     * Properly destroys each Angular ComponentRef to free all memory.
     */
    clearCacheForRoute(routeId: string): void {
        const toDelete: string[] = [];
        for (const key of this.cache.keys()) {
            if (key === routeId || key.startsWith(routeId + '/')) {
                toDelete.push(key);
            }
        }
        for (const key of toDelete) {
            this.destroyHandle(this.cache.get(key));
            this.cache.delete(key);
        }
        this.insertionOrder = this.insertionOrder.filter(k => !toDelete.includes(k));
    }

    /** Destroy every cached component tree — called on logout. */
    clearAllCache(): void {
        for (const handle of this.cache.values()) {
            this.destroyHandle(handle);
        }
        this.cache.clear();
        this.insertionOrder = [];
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    ngOnDestroy(): void {
        this.clearAllCache();
    }

    // ── Private helpers ──────────────────────────────────────────────────────

    /**
     * Build a stable string key from the route's full path segments.
     * e.g. /payments/list → 'payments/list'
     */
    private buildKey(route: ActivatedRouteSnapshot): string {
        return route.pathFromRoot
            .map(r => r.url.map(s => s.path).join('/'))
            .filter(Boolean)
            .join('/');
    }

    /** Routes that should always use a fresh component tree (no detach/attach cache). */
    private isNoReusePath(fullPath: string): boolean {
        return fullPath === 'stock-inventory'
            || fullPath.startsWith('stock-inventory/')
            || fullPath === 'production'
            || fullPath.startsWith('production/');
    }

    /** Tab system is only active on web/desktop, never on native apps. */
    private isEnabled(): boolean {
        if (Capacitor.isNativePlatform()) { return false; }
        if (typeof window === 'undefined') { return false; }
        return window.matchMedia('(min-width: 769px)').matches;
    }

    /**
     * Explicitly destroy the Angular ComponentRef embedded inside the
     * detached handle to release all subscriptions and DOM nodes.
     */
    private destroyHandle(handle: DetachedRouteHandle | undefined): void {
        if (!handle) { return; }
        try {
            const h = handle as any;
            if (h?.componentRef?.destroy) {
                h.componentRef.destroy();
            }
        } catch (_) {
            // Ignore errors during cleanup — component may already be destroyed
        }
    }
}
